/**
 * Notification preferences hook — server-synced via TanStack Query +
 * optimistic mutation. The toggle UI stays instant: `togglePref` flips
 * the cache before the network round-trip lands, and rolls back on
 * failure with a toast.
 *
 * The Zustand local cache (`useNotificationPrefsStore`) survives across
 * sessions for offline-first reads, and is hydrated by this hook on
 * mount so the values are eventually consistent with the server.
 */

import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  notificationPrefsService,
  DEFAULT_NOTIFICATION_PREFS,
  type NotificationPrefs,
} from '../services/notification-prefs.service'
import { useAuth } from './use-auth'
import { useNotificationPrefsStore } from '../stores/notification-prefs.store'
import { toast } from '../toast'
import { errorMessage } from '../utils/observability'

const STALE_MS = 60 * 1000

const notificationPrefsKeys = {
  all: ['notification-prefs'] as const,
  byUser: (userId: string) => [...notificationPrefsKeys.all, userId] as const,
}

type NotificationPrefKey = keyof Omit<NotificationPrefs, 'updatedAt'>

interface UseNotificationPrefsResult {
  prefs: Omit<NotificationPrefs, 'updatedAt'>
  isLoading: boolean
  isSyncing: boolean
  togglePref: (key: NotificationPrefKey) => void
  refetch: () => void
}

export function useNotificationPrefs(): UseNotificationPrefsResult {
  const { t } = useTranslation()
  const { user } = useAuth()
  const userId = user?.id ?? ''
  const queryClient = useQueryClient()
  const localStore = useNotificationPrefsStore()

  const query = useQuery({
    queryKey: notificationPrefsKeys.byUser(userId),
    queryFn: async () => {
      const fetched = await notificationPrefsService.getPrefs(userId)
      // Hydrate the Zustand cache so a future cold start has the
      // server-of-record values without a round-trip.
      if (fetched) localStore.hydrateFromServer(fetched)
      return fetched
    },
    enabled: !!userId,
    staleTime: STALE_MS,
  })

  const mutation = useMutation({
    mutationFn: (patch: Partial<NotificationPrefs>) =>
      notificationPrefsService.updatePrefs(userId, patch),

    onMutate: async (patch) => {
      const key = notificationPrefsKeys.byUser(userId)
      await queryClient.cancelQueries({ queryKey: key })

      const previous = queryClient.getQueryData<NotificationPrefs | null>(key)

      // Optimistic: flip the cache + Zustand local immediately.
      queryClient.setQueryData<NotificationPrefs | null>(key, (old) => {
        if (!old) return old
        return { ...old, ...patch, updatedAt: new Date().toISOString() }
      })
      localStore.applyPatch(patch)

      return { previous }
    },

    onError: (err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationPrefsKeys.byUser(userId), context.previous)
        localStore.hydrateFromServer(context.previous)
      }
      toast.error(errorMessage(err, t('notification_prefs.update_failed')))
    },
  })

  // Keep the Zustand cache in sync with the latest server data on each
  // successful refetch — covers the "another device changed prefs" case.
  useEffect(() => {
    if (query.data) localStore.hydrateFromServer(query.data)
    // localStore is a stable Zustand reference; intentional dep omission
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data])

  // Resolved prefs: prefer the freshest source of truth.
  // 1. Server query data if loaded.
  // 2. Zustand local cache (survived from a prior session).
  // 3. Defaults (first ever launch on this device, signed out, etc.).
  const prefs: Omit<NotificationPrefs, 'updatedAt'> = query.data
    ? stripUpdatedAt(query.data)
    : localStore.hasHydrated
      ? stripUpdatedAt(localStore.snapshot())
      : DEFAULT_NOTIFICATION_PREFS

  const togglePref = (key: NotificationPrefKey) => {
    const current = prefs[key]
    mutation.mutate({ [key]: !current } as Partial<NotificationPrefs>)
  }

  return {
    prefs,
    isLoading: query.isLoading,
    isSyncing: mutation.isPending || query.isFetching,
    togglePref,
    refetch: () => {
      void query.refetch()
    },
  }
}

function stripUpdatedAt(p: NotificationPrefs | Omit<NotificationPrefs, 'updatedAt'>) {
  if ('updatedAt' in p) {
    const rest: Partial<NotificationPrefs> = { ...p }
    delete rest.updatedAt
    return rest as Omit<NotificationPrefs, 'updatedAt'>
  }
  return p
}
